import { useState } from "react";
import { ArrowDownLeft, ArrowUpRight, Bot, Trash2 } from "lucide-react";
import { SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { AgentStatus, COMPANY_NAMES } from "./AgentBrainMap";

const SOURCES = { "not_connected": "Runtime not connected", app_worker: "Live app-managed worker" };

export function AgentHeader({ agent }) {
  return <><SheetHeader><span className="brain-eyebrow"><Bot size={16} /> AGENT INSPECTOR</span><SheetTitle data-testid="agent-details-name">{agent.name}</SheetTitle>
    <SheetDescription data-testid="agent-details-role">{agent.role}</SheetDescription></SheetHeader>
    <div className="brain-detail-state"><AgentStatus agent={agent} testId="agent-details-status" /><span>{SOURCES[agent.status_source] || "Stored status"}</span></div></>;
}

function AssignmentList({ label, items, testId }) {
  return <section className="brain-detail-section" data-testid={testId}><h3>{label}</h3>
    {items?.length ? <div className="brain-assignment-list">{[...new Set(items)].map((item) => <span key={item}>{item}</span>)}</div> : <p className="brain-unavailable">Not connected — no assignments recorded</p>}
  </section>;
}

export function AgentFacts({ agent, onClose, onCompany }) {
  return <><section className="brain-detail-section" data-testid="agent-details-description"><h3>Purpose</h3><p>{agent.description || "Detailed description not configured. The role above is from the agent record."}</p></section>
    {agent.workspace !== "global" && <button className="brain-detail-company" data-testid="agent-details-company" onClick={() => { onClose(); onCompany(agent.workspace); }}>{COMPANY_NAMES[agent.workspace]} <ArrowUpRight size={16} /></button>}
    <AssignmentList label="Assigned tools" items={agent.tools} testId="agent-details-tools" />
    <AssignmentList label="Assigned skills" items={agent.skills} testId="agent-details-skills" />
  </>;
}

export function AgentActivity({ agent }) {
  const task = agent.current_task, activity = agent.last_activity;
  return <><section className="brain-detail-section" data-testid="agent-details-task"><h3>Current task</h3>{task ? <><p>{task.title}</p><small>{task.status} · Recorded app-worker task</small></> : <p className="brain-unavailable">No current task recorded</p>}</section>
    <section className="brain-detail-section" data-testid="agent-details-activity"><h3>Last recorded activity</h3>{activity ? <><p>{activity.text}</p><small>{activity.at ? new Date(activity.at).toLocaleString() : "Timestamp not recorded"}{activity.source === "preview" ? " · PREVIEW" : ""}</small></> : <p className="brain-unavailable">Activity feed not connected</p>}</section></>;
}

function connectionLabel(connection) {
  if (connection.type === "reporting") return connection.direction === "incoming" ? "Reports to" : "Delegates to";
  return `${connection.label} · ${connection.bidirectional ? "two-way" : connection.direction}`;
}

export function AgentConnections({ agent, onInspect }) {
  return <section className="brain-detail-section" data-testid="agent-details-connections"><h3>Connections · {agent.connections.length}</h3>
    {agent.connections.map((connection) => <button key={`${connection.id}-${connection.direction}`} data-testid={`agent-connection-${connection.id}`} className="brain-connection-row" onClick={() => onInspect(connection.peer_id, connection.peer_workspace)}>
      {connection.direction === "outgoing" ? <ArrowUpRight size={16} /> : <ArrowDownLeft size={16} />}<span><strong>{connection.peer_name}</strong><small>{connectionLabel(connection)}</small></span>
    </button>)}
    {!agent.connections.length && <p className="brain-unavailable">No relationships recorded{agent.parent_label ? ` · unresolved parent: ${agent.parent_label}` : ""}</p>}
  </section>;
}

export function AgentCommands({ commands }) {
  if (!commands.length) return null;
  return <section className="brain-detail-section" data-testid="agent-details-commands"><h3>Configured command templates</h3><small>Only status, report and monitor are connected to the app worker. Other tools need explicit integration.</small><div className="brain-assignment-list">{[...new Set(commands)].map((command) => <span key={command}>{command}</span>)}</div></section>;
}

export function AgentRemoval({ agent, onDelete }) {
  const [confirm, setConfirm] = useState(false), [busy, setBusy] = useState(false);
  const remove = async () => {
    if (!confirm) { setConfirm(true); return; }
    setBusy(true);
    try { await onDelete(agent); } finally { setBusy(false); }
  };
  return <section className="brain-detail-section"><button className="brain-delete" data-testid="agent-details-delete" disabled={busy} onClick={remove}><Trash2 size={14} />{confirm ? "Confirm remove agent record" : "Remove agent"}</button>
    {confirm && <button className="brain-delete-cancel" data-testid="agent-delete-cancel" disabled={busy} onClick={() => setConfirm(false)}>Cancel</button>}</section>;
}
